// .pen BJddk 页面-资源包-查看所有文件 — 三入口共用:
//   local  创建页(rows=内存已选文件,无下钻,无订阅栏,无 packId → 无分享)
//   owner  独立页 /pack/manage/:id/files(rows=PackDetail items,packId 支持下钻,无订阅栏)
//   viewer /pack/:packId/files & /s/:packId/files(rows=已订阅 items 或 undefined→自拉 preview;未订阅显订阅栏)
// 卡片样式对齐 TH1Qz/A5gedY:68px 圆角卡 + 封面缩略 + 面包屑 + section 计数 + 排序 pill。
// 封面对文件和文件夹都生效(后端 itemCoverThumb 对 cover_file_info_id/图片自身缩略签名)。
// 排序:前端本地排序(数据量小;服务端无排序参数,见 specs §6)。
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronRight, Crown, ArrowUpDown, Share2 } from 'lucide-react'
import { formatBytes, cn, copyToClipboard } from '@dogdisk/shared'
import { PackItemThumb } from './pack-item-thumb'
import { TopBar } from './mobile-screen'
import { SortFilterSheet } from './modals/sort-filter-sheet'
import type { SortState } from '../_lib/file-sort'
import { fetchItemsPreview, type PreviewItem } from '../_lib/pack-api'

export type PackFileRow = {
  key: string
  itemId?: string | undefined
  name: string
  type: string
  size: number | null
  mimeType: string | null
  coverThumbUrl?: string | null
}

type Props = {
  title?: string
  onBack: () => void
  rows?: PackFileRow[] | undefined
  packId?: string | undefined
  subscribeBar?: { priceCoin: number; onSubscribe: () => void } | undefined
  onRowClick?: ((row: PackFileRow) => void) | undefined
}

export function PackFilesView({ title = '资源包文件列表', onBack, rows, packId, subscribeBar, onRowClick }: Props) {
  const [sort, setSort] = useState<SortState>({ sortBy: 'name', order: 'asc' })
  const [showSort, setShowSort] = useState(false)
  const [stack, setStack] = useState<{ itemId: string; path: string; label: string }[]>([])
  const top = stack[stack.length - 1]

  const previewQuery = useQuery({
    queryKey: ['pack', 'items-preview', packId ?? null, top?.itemId ?? null, top?.path ?? ''] as const,
    enabled: Boolean(packId) && (rows === undefined || stack.length > 0),
    queryFn: () => fetchItemsPreview(packId!, top ? { root: top.itemId, path: top.path } : {}),
  })

  const baseRows: PackFileRow[] = useMemo(() => {
    if (stack.length === 0 && rows) return rows
    return (previewQuery.data ?? []).map((it: PreviewItem, i: number) => ({
      key: it.item_id ?? `${it.name}-${i}`,
      itemId: it.item_id,
      name: it.name,
      type: it.type,
      size: it.size_original,
      mimeType: it.mime_type,
      coverThumbUrl: it.cover_thumb_url ?? null,
    }))
  }, [rows, stack.length, previewQuery.data])

  const sorted = useMemo(() => {
    const dir = sort.order === 'asc' ? 1 : -1
    return [...baseRows].sort((a, b) => {
      if (a.type !== b.type) return a.type === 'folder' ? -1 : 1
      if (sort.sortBy === 'size') return ((a.size ?? 0) - (b.size ?? 0)) * dir
      if (sort.sortBy === 'type') return (a.mimeType ?? '').localeCompare(b.mimeType ?? '') * dir
      return a.name.localeCompare(b.name) * dir
    })
  }, [baseRows, sort])

  const enterFolder = (row: PackFileRow) => {
    if (!packId) return
    if (stack.length === 0) {
      if (!row.itemId) return
      setStack([{ itemId: row.itemId, path: '', label: row.name }])
    } else {
      const cur = stack[stack.length - 1]!
      setStack([...stack, { itemId: cur.itemId, path: cur.path ? `${cur.path}/${row.name}` : row.name, label: row.name }])
    }
  }

  const loading = (stack.length > 0 || rows === undefined) && previewQuery.isLoading
  const canEnter = (row: PackFileRow): boolean =>
    row.type === 'folder' && Boolean(packId) && (stack.length > 0 || Boolean(row.itemId))

  // 面包屑:根「全部文件」+ 下钻各层 label;点击非末项跳回该层(0=根 → setStack([]))。
  const crumbs = ['全部文件', ...stack.map((s) => s.label)]

  return (
    <div data-testid="BJddk" className="fixed inset-0 z-40 mx-auto flex max-w-[402px] flex-col bg-white">
      <TopBar
        title={top ? top.label : title}
        onBack={() => {
          if (stack.length > 0) setStack(stack.slice(0, -1))
          else onBack()
        }}
        right={
          packId ? (
            <button
              type="button"
              data-testid="BJddk-share-btn"
              aria-label="分享"
              onClick={() => void copyToClipboard(`${window.location.origin}/#/pack/${packId}`)}
              className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#F6F7F8] active:bg-[#ECEEF1]"
            >
              <Share2 size={18} className="text-[#1A1A1A]" aria-hidden="true" />
            </button>
          ) : undefined
        }
      />

      {stack.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 px-6 pt-1 pb-1 text-[13px]" data-testid="BJddk-breadcrumb">
          {crumbs.map((label, i) => {
            const isLast = i === crumbs.length - 1
            return (
              <span key={i} className="flex items-center gap-1.5">
                {i > 0 && <ChevronRight size={12} className="text-[#D1D5DB]" aria-hidden="true" />}
                <button
                  type="button"
                  disabled={isLast}
                  onClick={() => setStack(stack.slice(0, i))}
                  className={isLast ? 'font-semibold text-[#1A1A1A]' : 'font-medium text-[#19caad]'}
                >
                  {label}
                </button>
              </span>
            )
          })}
        </div>
      )}

      <div className="flex items-center justify-between px-6 pb-2 pt-1">
        <span className="flex items-center gap-2">
          <span className="text-[17px] font-semibold text-[#1A1A1A]">文件列表</span>
          <span data-testid="BJddk-count" className="text-[13px] text-[#9CA3AF]">{sorted.length} 项</span>
        </span>
        <button
          type="button"
          data-testid="BJddk-sort-btn"
          aria-label="排序"
          onClick={() => setShowSort(true)}
          className="flex items-center gap-1.5 rounded-[14px] bg-[#F6F7F8] px-2.5 py-1.5 text-[12px] font-medium text-[#6B7280] active:bg-[#ECEEF1]"
        >
          <ArrowUpDown size={14} aria-hidden="true" />
          排序
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-6 pb-6">
        {loading ? (
          <p className="py-10 text-center text-[14px] text-[#9CA3AF]">加载中…</p>
        ) : (
          <ul className="flex flex-col gap-2.5">
            {sorted.map((row) => {
              const enterable = canEnter(row)
              const clickable = enterable || (row.type !== 'folder' && Boolean(onRowClick))
              return (
                <li key={row.key}>
                  <button
                    type="button"
                    disabled={!clickable}
                    onClick={() => {
                      if (enterable) enterFolder(row)
                      else onRowClick?.(row)
                    }}
                    className={cn(
                      'flex h-[68px] w-full items-center gap-3 rounded-[14px] bg-[#F6F7F8] px-3.5 text-left',
                      clickable ? 'active:bg-[#ECEEF1]' : '',
                    )}
                  >
                    <PackItemThumb
                      coverThumbUrl={row.coverThumbUrl}
                      type={row.type}
                      mimeType={row.mimeType}
                      name={row.name}
                      size={40}
                      iconSize={20}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[15px] font-semibold text-[#1A1A1A]">{row.name}</span>
                      <span className="block text-[12px] text-[#9CA3AF]">
                        {row.type === 'folder' ? '文件夹' : formatBytes(row.size ?? 0)}
                      </span>
                    </span>
                    {enterable && <ChevronRight size={18} className="shrink-0 text-[#9CA3AF]" aria-hidden="true" />}
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </div>

      {subscribeBar && (
        <div className="border-t border-[#F3F4F6] px-6 pt-3 pb-safe">
          <button
            type="button"
            data-testid="BJddk-subscribe-btn"
            onClick={subscribeBar.onSubscribe}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#1A1A1A] text-[15px] font-semibold text-white active:bg-black"
          >
            <Crown size={18} className="text-[#F59E0B]" aria-hidden="true" />
            立即订阅 · {subscribeBar.priceCoin} 金币
          </button>
        </div>
      )}

      <SortFilterSheet
        open={showSort}
        onClose={() => setShowSort(false)}
        value={sort}
        onChange={setSort}
        options={['name', 'size', 'type']}
      />
    </div>
  )
}
